import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { NbDisplayDataOutput, NbExecuteResultOutput, NbOutput } from './nbformat'
import { joinMultiline, sanitizeHtml, stripAnsi } from './outputUtils'
import { PLOTLY_JS_PATH } from '../PythonNotebookV2/runtime/plotlyWheel'
import { resolveJsDelivrRootFromWindow } from '../PythonNotebookV2/runtime/pyodideVersion'
import './notebookOutputs.css'

const REMARK_PLUGINS = [remarkGfm]

const PLOTLY_MIME = 'application/vnd.plotly.v1+json'

/** Sandboxed iframe so plotly.js can run without touching the app's DOM. */
function PlotlyOutput({ figure }: { figure: unknown }) {
  const layout = (figure as { layout?: { height?: unknown } })?.layout
  const height = typeof layout?.height === 'number' ? layout.height : 450
  const json = JSON.stringify(figure).replace(/</g, '\\u003c')
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><script src="${resolveJsDelivrRootFromWindow()}${PLOTLY_JS_PATH}"></script><style>html,body{margin:0;height:100%}</style></head><body><script>var f=${json};Plotly.newPlot(document.body,f.data,f.layout,Object.assign({responsive:true},f.config))</script></body></html>`
  return <iframe title="plotly" sandbox="allow-scripts" srcDoc={srcDoc} className="nb-output-plotly" style={{ width: '100%', height, border: 0 }} />
}

function RichOutput({ data }: { data: (NbDisplayDataOutput | NbExecuteResultOutput)['data'] }) {
  if (data[PLOTLY_MIME]) {
    return <PlotlyOutput figure={data[PLOTLY_MIME]} />
  }
  if (data['image/png']) {
    return <img alt="output" src={`data:image/png;base64,${joinMultiline(data['image/png'])}`} className="nb-output-image" />
  }
  if (data['image/jpeg']) {
    return <img alt="output" src={`data:image/jpeg;base64,${joinMultiline(data['image/jpeg'])}`} className="nb-output-image" />
  }
  if (data['image/svg+xml']) {
    return <div className="nb-output-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(joinMultiline(data['image/svg+xml'])) }} />
  }
  if (data['text/html']) {
    return <div className="nb-output-html overflow-x-auto" dangerouslySetInnerHTML={{ __html: sanitizeHtml(joinMultiline(data['text/html'])) }} />
  }
  if (data['text/markdown']) {
    return (
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{joinMultiline(data['text/markdown'])}</ReactMarkdown>
      </div>
    )
  }
  if (data['text/plain']) {
    return <pre className="nb-output-text">{joinMultiline(data['text/plain'])}</pre>
  }
  return null
}

export function NotebookOutput({ output }: { output: NbOutput }) {
  if (output.output_type === 'stream') {
    const isStderr = output.name === 'stderr'
    return (
      <pre
        className={isStderr ? 'nb-output-text nb-output-stderr' : 'nb-output-text'}
        data-mime-type={isStderr ? 'application/vnd.jupyter.stderr' : undefined}
      >
        {joinMultiline(output.text)}
      </pre>
    )
  }
  if (output.output_type === 'execute_result' || output.output_type === 'display_data') {
    return <RichOutput data={output.data || {}} />
  }
  if (output.output_type === 'error') {
    const traceback = (output.traceback || []).map(stripAnsi).join('\n')
    return <pre className="nb-output-text nb-output-error">{traceback || `${output.ename}: ${output.evalue}`}</pre>
  }
  return null
}
